import { useState, useMemo, useCallback, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ArrowLeft, ChevronRight, Plus, X, Loader2, Mail, Trash2, CopyPlus } from 'lucide-react'
import PageLayout from '@/components/layout/PageLayout'
import { useUIv2 } from '@/hooks/useUIv2'
import WizardLayout from './WizardLayout'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import { useTranslation } from '@/i18n'
import { apiService } from '@/service/api'
import { useAppStore } from '@/store'
import { toast } from 'sonner'
import { getUserTimezone, interviewWindowUTC } from './scheduling-utils'
import ScheduleInterviewField from './ScheduleInterviewPicker'


interface Question {
  id: string
  text: string
}

interface Expert {
  id: string
  email: string
}

interface InterviewForm {
  id: string
  topic: string
  questions: Question[]
  experts: Expert[]
  date: string
  startTime: string
  endTime: string
  duration: number
  timezone: string
  immediate?: boolean
}


const makeBlankInterview = (id: string, defaultExpertEmail = ''): InterviewForm => ({
  id,
  topic: '',
  questions: [
    { id: '1', text: '' },
    { id: '2', text: '' },
    { id: '3', text: '' },
  ],
  experts: [{ id: '1', email: defaultExpertEmail }],
  date: '',
  startTime: '',
  endTime: '',
  duration: 30,
  timezone: getUserTimezone(),
})



const SetupInterviews = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const { user } = useAppStore()
  const formData = location.state?.formData || {}
  const coworkerId = location.state?.coworkerId as string | undefined

  // Auto-fill expert email with user's own email when building for self
  const defaultExpertEmail = useMemo(
    () => (formData.type === 'me' ? user?.email ?? '' : ''),
    [formData.type, user?.email],
  )

  const [interviews, setInterviews] = useState<InterviewForm[]>([
    makeBlankInterview('1', defaultExpertEmail),
  ])
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Backfill expert email if user loaded after initial render
  useEffect(() => {
    if (!defaultExpertEmail) return
    setInterviews((prev) =>
      prev.map((interview) =>
        interview.experts[0]?.email === ''
          ? {
              ...interview,
              experts: interview.experts.map((e, i) =>
                i === 0 ? { ...e, email: defaultExpertEmail } : e
              ),
            }
          : interview
      )
    )
  }, [defaultExpertEmail])

  const handleBack = () => {
    navigate('/digital-coworker/connect-accounts', {
      state: { formData, coworkerId },
    })
  }

  const handleAddInterview = () => {
    setInterviews((prev) => [
      ...prev,
      makeBlankInterview(String(prev.length + 1), defaultExpertEmail),
    ])
  }

  const handleRemoveInterview = (interviewId: string) => {
    setInterviews((prev) => prev.filter((i) => i.id !== interviewId))
  }

  const updateInterview = <K extends keyof InterviewForm>(
    interviewId: string,
    field: K,
    value: InterviewForm[K]
  ) => {
    setInterviews((prev) =>
      prev.map((i) => (i.id === interviewId ? { ...i, [field]: value } : i))
    )
  }

  const updateInterviewSchedule = useCallback(
    (
      interviewId: string,
      schedule: Pick<
        InterviewForm,
        'date' | 'startTime' | 'endTime' | 'duration' | 'timezone' | 'immediate'
      >
    ) => {
      setInterviews((prev) =>
        prev.map((i) =>
          i.id === interviewId ? { ...i, ...schedule } : i
        )
      )
    },
    []
  )

  // Question helpers
  const handleAddQuestion = (interviewId: string) => {
    setInterviews((prev) =>
      prev.map((i) => {
        if (i.id !== interviewId) return i
        const maxId = i.questions.reduce(
          (max, q) => Math.max(max, parseInt(q.id, 10) || 0),
          0
        )
        return {
          ...i,
          questions: [...i.questions, { id: String(maxId + 1), text: '' }],
        }
      })
    )
  }

  const handleUpdateQuestion = (
    interviewId: string,
    questionId: string,
    text: string
  ) => {
    setInterviews((prev) =>
      prev.map((i) =>
        i.id === interviewId
          ? {
              ...i,
              questions: i.questions.map((q) =>
                q.id === questionId ? { ...q, text } : q
              ),
            }
          : i
      )
    )
  }

  const handleRemoveQuestion = (interviewId: string, questionId: string) => {
    setInterviews((prev) =>
      prev.map((i) =>
        i.id === interviewId
          ? { ...i, questions: i.questions.filter((q) => q.id !== questionId) }
          : i
      )
    )
  }

  // Expert helpers
  const handleAddExpert = (interviewId: string) => {
    setInterviews((prev) =>
      prev.map((i) => {
        if (i.id !== interviewId) return i
        const maxId = i.experts.reduce(
          (max, e) => Math.max(max, parseInt(e.id, 10) || 0),
          0
        )
        return {
          ...i,
          experts: [...i.experts, { id: String(maxId + 1), email: '' }],
        }
      })
    )
  }

  const handleUpdateExpert = (
    interviewId: string,
    expertId: string,
    email: string
  ) => {
    setInterviews((prev) =>
      prev.map((i) =>
        i.id === interviewId
          ? {
              ...i,
              experts: i.experts.map((e) =>
                e.id === expertId ? { ...e, email } : e
              ),
            }
          : i
      )
    )
  }

  const handleRemoveExpert = (interviewId: string, expertId: string) => {
    setInterviews((prev) =>
      prev.map((i) =>
        i.id === interviewId
          ? { ...i, experts: i.experts.filter((e) => e.id !== expertId) }
          : i
      )
    )
  }

  const validate = (): string | null => {
    for (const interview of interviews) {
      if (!interview.topic.trim())
        return t('digital_coworker:setup_interviews.validation.topic_required')
      if (!interview.date)
        return t('digital_coworker:setup_interviews.validation.date_required')
      if (!interview.startTime)
        return t('digital_coworker:setup_interviews.validation.start_time_required')
      if (!interview.endTime)
        return t('digital_coworker:setup_interviews.validation.end_time_required')
      if (interview.startTime >= interview.endTime)
        return t('digital_coworker:setup_interviews.validation.end_after_start')
    }
    return null
  }

  const handleCreateInterviews = async () => {
    const error = validate()
    if (error) {
      toast.error(error)
      return
    }

    setIsSubmitting(true)
    let allSucceeded = true

    for (const interview of interviews) {
      try {
        const { start: startUTC, end: endUTC } = interviewWindowUTC(interview)

        await apiService.createInterview({
          topic: interview.topic.trim(),
          questions: interview.questions
            .map((q) => q.text.trim())
            .filter(Boolean),
          interview_start_date: startUTC,
          interview_end_date: endUTC,
          experts: interview.experts
            .filter((e) => e.email.trim())
            .map((e) => ({ name: '', email: e.email.trim() })),
          digital_coworker_id: coworkerId,
        })
      } catch (err) {
        const detail = err instanceof Error ? err.message : ''
        console.error('[setup-interviews] createInterview failed', { topic: interview.topic, err })
        toast.error(
          [
            t('digital_coworker:setup_interviews.toast.create_error', {
              topic: interview.topic || `#${interview.id}`,
            }),
            detail,
          ]
            .filter(Boolean)
            .join(' — ')
        )
        allSucceeded = false
      }
    }

    setIsSubmitting(false)

    if (allSucceeded) {
      toast.success(t('digital_coworker:setup_interviews.toast.create_success'))
      if (isV2) {
        navigate('/digital-coworker/success', {
          state: { coworkerName: formData?.name },
        })
      } else {
        navigate('/digital-coworker')
      }
    }
  }

  const isV2 = useUIv2()

  const header = (
    <div>
      <h2 className="text-lg font-semibold mb-2">
        {t('digital_coworker:setup_interviews.title')}
      </h2>
    </div>
  )

  const interviewContent = (
        <>
          {/* Interview Cards */}
          <div className="space-y-6">
            {interviews.map((interview, interviewIndex) => (
              <div
                key={interview.id}
                className="border rounded-lg p-6 space-y-5"
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-sm">
                    {t('digital_coworker:setup_interviews.interview_label', {
                      number: interviewIndex + 1,
                    })}
                  </h3>
                  {interviews.length > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-gray-400 dark:text-gray-500 hover:text-red-500"
                      onClick={() => handleRemoveInterview(interview.id)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                {/* Topic */}
                <div className="space-y-2">
                  <Label htmlFor={`topic-${interview.id}`}>
                    {t('digital_coworker:setup_interviews.topic_label')}
                  </Label>
                  <Input
                    id={`topic-${interview.id}`}
                    placeholder={t(
                      'digital_coworker:setup_interviews.topic_placeholder'
                    )}
                    value={interview.topic}
                    onChange={(e) =>
                      updateInterview(interview.id, 'topic', e.target.value)
                    }
                  />
                </div>

                {/* Interview Platform — Meet / Zoom / Teams toggle */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold">
                      {t('digital_coworker:setup_interviews.platform_label', 'Interview Platform')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t('digital_coworker:setup_interviews.platform_desc', 'Select an interview platform')}
                    </p>
                  </div>
                  <div className="flex items-center rounded-lg bg-muted p-[3px]">
                    {/* Meet — selected (currently working) */}
                    <button
                      type="button"
                      className="flex items-center gap-1.5 rounded-md bg-white dark:bg-dark-card px-3 py-1.5 text-xs font-medium shadow-sm border border-transparent"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" className="shrink-0">
                        <path d="M9.05078 8.00001L10.6104 9.69937L12.7075 10.9769L13.0732 8.01047L12.7075 5.11023L10.5701 6.23268L9.05078 8.00001Z" fill="#00832D"/>
                        <path d="M0 10.7015V13.2287C0 13.8065 0.490915 14.2745 1.09702 14.2745H3.74814L4.29665 12.3642L3.74814 10.7015L1.92892 10.1786L0 10.7015Z" fill="#0066DA"/>
                        <path d="M3.74814 1.72546L0 5.29847L1.92892 5.82135L3.74814 5.29847L4.28751 3.65837L3.74814 1.72546Z" fill="#E94235"/>
                        <path d="M3.74814 5.29858H0V10.7017H3.74814V5.29858Z" fill="#2684FC"/>
                        <path d="M15.1022 3.23834L12.707 5.11025V10.977L15.1131 12.8576C15.4733 13.126 15.9999 12.8811 15.9999 12.4445V3.6427C15.9999 3.20087 15.4614 2.9586 15.1022 3.23834ZM9.05029 8.00003V10.7016H3.74805V14.2746H11.61C12.2161 14.2746 12.707 13.8066 12.707 13.2288V10.977L9.05029 8.00003Z" fill="#00AC47"/>
                        <path d="M11.61 1.72546H3.74805V5.29847H9.05029V8.00002L12.707 5.11198V2.77122C12.707 2.19344 12.2161 1.72546 11.61 1.72546Z" fill="#FFBA00"/>
                      </svg>
                      Meet
                    </button>
                    {/* Zoom — disabled */}
                    <button
                      type="button"
                      disabled
                      className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-muted-foreground cursor-not-allowed"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" className="shrink-0">
                        <g clipPath="url(#clip0_zoom)">
                          <path d="M16 8.00001C16 8.85551 15.9312 9.69436 15.7999 10.5123C15.3649 13.2201 13.2201 15.365 10.5123 15.7999C9.6943 15.9312 8.85544 16 8 16C7.14456 16 6.3057 15.9312 5.48774 15.7999C2.77995 15.365 0.635051 13.2201 0.200143 10.5123C0.0687714 9.69436 0 8.8555 0 8.00001C0 7.14452 0.0687714 6.30565 0.200143 5.48772C0.635057 2.77993 2.77995 0.635041 5.48774 0.200143C6.3057 0.0687715 7.14457 0 8.00001 0C8.85545 0 9.69431 0.0687715 10.5123 0.200143C13.2201 0.635041 15.365 2.77993 15.7999 5.48771C15.9312 6.30565 16 7.14452 16 8.00001Z" fill="#0B5CFF"/>
                          <path d="M9.71443 9.99997C9.71443 10.4733 9.33067 10.8571 8.85728 10.8571H5.143C4.19622 10.8571 3.42871 10.0896 3.42871 9.14283V5.99997C3.42871 5.52661 3.81247 5.14282 4.28585 5.14282H8.00014C8.94692 5.14282 9.71443 5.91033 9.71443 6.85711V9.99997ZM11.8859 5.65706L10.6287 6.59992C10.4129 6.7618 10.2858 7.01585 10.2858 7.28565L10.2859 8.71422C10.2859 8.984 10.4129 9.23804 10.6287 9.39992L11.8859 10.3428C12.1684 10.5547 12.5716 10.3531 12.5716 9.99992V5.99992C12.5716 5.64676 12.1684 5.44516 11.8859 5.65706Z" fill="white"/>
                        </g>
                        <defs><clipPath id="clip0_zoom"><rect width="16" height="16" fill="white"/></clipPath></defs>
                      </svg>
                      Zoom
                    </button>
                    {/* Teams — disabled */}
                    <button
                      type="button"
                      disabled
                      className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-muted-foreground cursor-not-allowed"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="15" viewBox="0 0 16 15" fill="none" className="shrink-0">
                        <g clipPath="url(#clip0_teams)">
                          <path d="M11.1605 5.5H15.2933C15.6838 5.5 16.0003 5.8119 16.0003 6.19666V9.90623C16.0003 11.3203 14.837 12.4667 13.402 12.4667H13.3897C11.9547 12.4669 10.7912 11.3207 10.791 9.90659C10.791 9.90647 10.791 9.90635 10.791 9.90623V5.8641C10.791 5.66301 10.9564 5.5 11.1605 5.5Z" fill="#5059C9"/>
                          <path d="M13.9537 4.76668C14.8785 4.76668 15.6281 4.02795 15.6281 3.11668C15.6281 2.20541 14.8785 1.46667 13.9537 1.46667C13.029 1.46667 12.2793 2.20541 12.2793 3.11668C12.2793 4.02795 13.029 4.76668 13.9537 4.76668Z" fill="#5059C9"/>
                          <path d="M8.7438 4.76667C10.0796 4.76667 11.1624 3.69962 11.1624 2.38334C11.1624 1.06706 10.0796 0 8.7438 0C7.40804 0 6.3252 1.06706 6.3252 2.38334C6.3252 3.69962 7.40804 4.76667 8.7438 4.76667Z" fill="#7B83EB"/>
                          <path d="M11.969 5.5H5.14707C4.76127 5.50941 4.45607 5.82491 4.46503 6.2051V10.4361C4.41115 12.7176 6.24287 14.6108 8.55805 14.6667C10.8732 14.6108 12.7049 12.7176 12.6511 10.4361V6.2051C12.66 5.82491 12.3548 5.50941 11.969 5.5Z" fill="#7B83EB"/>
                          <path opacity="0.1" d="M8.93016 5.5V11.429C8.92831 11.7009 8.76111 11.9451 8.50597 12.0487C8.42474 12.0825 8.33743 12.1 8.24923 12.1H4.79248C4.74411 11.979 4.69946 11.858 4.66225 11.7333C4.532 11.3126 4.46554 10.8752 4.46504 10.4353V6.204C4.45609 5.82442 4.76078 5.50941 5.14597 5.5H8.93016Z" fill="black"/>
                          <path opacity="0.2" d="M8.55807 5.5V11.7957C8.55805 11.8826 8.54034 11.9686 8.50597 12.0487C8.40092 12.3001 8.15305 12.4648 7.87714 12.4667H4.96737C4.90411 12.3457 4.84458 12.2247 4.79248 12.1C4.74039 11.9753 4.69946 11.858 4.66225 11.7333C4.532 11.3126 4.46554 10.8752 4.46504 10.4353V6.204C4.45609 5.82442 4.76078 5.50941 5.14597 5.5H8.55807Z" fill="black"/>
                          <path opacity="0.2" d="M8.55806 5.5V11.0623C8.55523 11.4318 8.25202 11.7305 7.87713 11.7333H4.66225C4.532 11.3126 4.46554 10.8752 4.46504 10.4353V6.204C4.45609 5.82442 4.76078 5.50941 5.14597 5.5H8.55806Z" fill="black"/>
                          <path opacity="0.2" d="M8.18596 5.5V11.0623C8.18313 11.4318 7.87992 11.7305 7.50503 11.7333H4.66225C4.532 11.3126 4.46554 10.8752 4.46504 10.4353V6.204C4.45609 5.82442 4.76078 5.50941 5.14597 5.5H8.18596Z" fill="black"/>
                          <path opacity="0.1" d="M8.93026 3.60434V4.75934C8.867 4.76301 8.80747 4.76668 8.74421 4.76668C8.68095 4.76668 8.62142 4.76301 8.55816 4.75934C8.43256 4.75113 8.308 4.73149 8.18607 4.70068C7.43258 4.52484 6.81007 4.00411 6.51165 3.30001C6.4603 3.18177 6.42044 3.059 6.39258 2.93335H8.24932C8.6248 2.93475 8.92883 3.23434 8.93026 3.60434Z" fill="black"/>
                          <path opacity="0.2" d="M8.55823 3.97104V4.75938C8.43263 4.75116 8.30807 4.73153 8.18614 4.70071C7.43265 4.52488 6.81014 4.00415 6.51172 3.30005H7.8773C8.25277 3.30145 8.5568 3.60105 8.55823 3.97104Z" fill="black"/>
                          <path opacity="0.2" d="M8.55823 3.97104V4.75938C8.43263 4.75116 8.30807 4.73153 8.18614 4.70071C7.43265 4.52488 6.81014 4.00415 6.51172 3.30005H7.8773C8.25277 3.30145 8.5568 3.60105 8.55823 3.97104Z" fill="black"/>
                          <path opacity="0.2" d="M8.18614 3.97105V4.70071C7.43265 4.52488 6.81014 4.00415 6.51172 3.30005H7.50521C7.88069 3.30146 8.18471 3.60105 8.18614 3.97105Z" fill="black"/>
                          <path d="M0.682043 3.30005H7.50399C7.88068 3.30005 8.18604 3.60096 8.18604 3.97215V10.6946C8.18604 11.0658 7.88067 11.3667 7.50399 11.3667H0.682043C0.305358 11.3667 0 11.0658 0 10.6946V3.97215C0 3.60096 0.305365 3.30005 0.682043 3.30005Z" fill="url(#paint0_teams)"/>
                          <path d="M5.88781 5.85855H4.52408V9.51788H3.65525V5.85855H2.29785V5.14868H5.88781V5.85855Z" fill="white"/>
                        </g>
                        <defs>
                          <linearGradient id="paint0_teams" x1="1.42208" y1="2.77488" x2="6.64712" y2="11.9584" gradientUnits="userSpaceOnUse">
                            <stop stopColor="#5A62C3"/><stop offset="0.5" stopColor="#4D55BD"/><stop offset="1" stopColor="#3940AB"/>
                          </linearGradient>
                          <clipPath id="clip0_teams"><rect width="16" height="14.6667" fill="white"/></clipPath>
                        </defs>
                      </svg>
                      Teams
                    </button>
                  </div>
                </div>

                {/* Schedule Interview */}
                <ScheduleInterviewField
                  schedule={interview}
                  onChange={(schedule) =>
                    updateInterviewSchedule(interview.id, schedule)
                  }
                />

                {/* Experts */}
                <div className="space-y-2">
                  <Label>{t('digital_coworker:setup_interviews.expert_emails_label')} <span className="text-gray-400 dark:text-gray-500 font-normal">{t('digital_coworker:setup_interviews.optional')}</span></Label>
                  <p className="text-xs text-gray-500 dark:text-dark-text-muted">
                    {t('digital_coworker:setup_interviews.expert_emails_desc')}
                  </p>
                  {interview.experts.map((expert) => (
                    <div key={expert.id} className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <Mail
                          size={15}
                          className="absolute left-2.5 top-2.5 text-gray-400 dark:text-gray-500"
                        />
                        <Input
                          type="email"
                          placeholder={t('digital_coworker:setup_interviews.expert_placeholder')}
                          value={expert.email}
                          onChange={(e) =>
                            handleUpdateExpert(
                              interview.id,
                              expert.id,
                              e.target.value
                            )
                          }
                          className="pl-8"
                        />
                      </div>
                      {interview.experts.length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-gray-400 dark:text-gray-500 hover:text-red-500"
                          onClick={() =>
                            handleRemoveExpert(interview.id, expert.id)
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1 text-sm"
                    onClick={() => handleAddExpert(interview.id)}
                  >
                    <Plus className="h-4 w-4" />
                    {t('digital_coworker:setup_interviews.add_expert')}
                  </Button>
                </div>

                {/* Questions */}
                <div className="space-y-3">
                  <div>
                    <Label>
                      {t(
                        'digital_coworker:setup_interviews.starting_questions_label'
                      )}{' '}
                      <span className="text-gray-400 dark:text-gray-500 font-normal">{t('digital_coworker:setup_interviews.optional')}</span>
                    </Label>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {t(
                        'digital_coworker:setup_interviews.starting_questions_desc'
                      )}
                    </p>
                  </div>

                  {interview.questions.map((question, questionIndex) => (
                    <div key={question.id} className="flex items-center gap-2">
                      <span className="text-gray-400 dark:text-gray-500 text-sm w-5 flex-shrink-0">
                        {questionIndex + 1}.
                      </span>
                      <Input
                        placeholder={t(
                          'digital_coworker:setup_interviews.question_placeholder'
                        )}
                        value={question.text}
                        onChange={(e) =>
                          handleUpdateQuestion(
                            interview.id,
                            question.id,
                            e.target.value
                          )
                        }
                        className="flex-1"
                      />
                      {interview.questions.length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            handleRemoveQuestion(interview.id, question.id)
                          }
                          className="h-8 w-8 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleAddQuestion(interview.id)}
                    className="gap-2 text-sm"
                  >
                    <Plus className="h-4 w-4" />
                    {t('digital_coworker:setup_interviews.add_question')}
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Add Interview */}
          <Button
            variant="outline"
            onClick={handleAddInterview}
            className="w-full gap-2"
          >
            <CopyPlus className="h-4 w-4" />
            {t('digital_coworker:setup_interviews.add_interview')}
          </Button>
        </>
  )

  if (isV2) {
    return (
      <WizardLayout step={3}>
        {header}
        <div className="space-y-6 mt-6">
          {interviewContent}
        </div>
        <div className="flex justify-between items-center pt-8">
          <IntuiButton variant="outline" leftIcon={<ArrowLeft />} onClick={handleBack} disabled={isSubmitting}>
            {t('digital_coworker:buttons.back', 'Back')}
          </IntuiButton>
          <IntuiButton
            onClick={handleCreateInterviews}
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            {isSubmitting
              ? t('digital_coworker:setup_interviews.creating')
              : t('digital_coworker:setup_interviews.create_button', 'Create Digital Coworker')}
          </IntuiButton>
        </div>
      </WizardLayout>
    )
  }

  return (
    <PageLayout>
      <div className="w-full max-w-3xl mx-auto py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-8 text-sm">
          <button
            onClick={() => navigate('/digital-coworker')}
            className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-dark-text transition-colors"
          >
            {t('digital_coworker:breadcrumb.list')}
          </button>
          <ChevronRight className="h-4 w-4 text-gray-400 dark:text-gray-500" />
        </div>

        <div className="space-y-8">
          {header}
          {interviewContent}

          {/* Footer */}
          <div className="flex justify-between items-center pt-6 border-t">
            <Button
              variant="ghost"
              onClick={handleBack}
              className="gap-2"
              disabled={isSubmitting}
            >
              <ArrowLeft className="h-4 w-4" />
              {t('digital_coworker:buttons.back')}
            </Button>
            <Button
              onClick={handleCreateInterviews}
              className="gap-2"
              disabled={isSubmitting}
            >
              {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {isSubmitting
                ? t('digital_coworker:setup_interviews.creating')
                : t('digital_coworker:setup_interviews.create_button')}
            </Button>
          </div>
        </div>
      </div>
    </PageLayout>
  )
}

export default SetupInterviews
